import axios from "axios";
import appConfig from "configs/app.config";
import { TOKEN_TYPE, REQUEST_HEADER_AUTH_KEY } from "constants/api.constant";

import { PERSIST_STORE_NAME } from "constants/app.constant";

import deepParseJson from "utils/deepParseJson";

import store from "../store";

import { onSignOutSuccess } from "../store/auth/sessionSlice";

import {
  enableNotification,
  disableNotification,
} from "store/notificationSlice";

const unauthorizedCode = [401];

const BaseService = axios.create({
  timeout: 60000,
  baseURL: appConfig.apiPrefix,
});

/*
-----------------------------------------
GET TOKEN SAFELY
-----------------------------------------
priority:
1. redux state (instant after login)
2. persisted localStorage
*/
function getAccessToken() {
  try {
    // FIRST check redux store
    const state = store.getState();

    const tokenFromState = state?.auth?.session?.token;

    if (tokenFromState) {
      return tokenFromState;
    }

    // fallback persisted storage
    const raw = localStorage.getItem(PERSIST_STORE_NAME);

    const parsed = deepParseJson(raw) || {};

    return parsed?.auth?.session?.token;
  } catch {
    return undefined;
  }
}

/*
-----------------------------------------
MAINTENANCE HANDLER
-----------------------------------------
*/
function handleMaintenance(systemMode = {}) {
  try {
    store.dispatch(onSignOutSuccess());

    localStorage.setItem("app_under_maintenance", "true");

    localStorage.setItem(
      "app_under_maintenance_message",
      systemMode.message || "System is under maintenance",
    );
  } catch {
    // silent fail
  }
}

/*
-----------------------------------------
REQUEST INTERCEPTOR
-----------------------------------------
*/
BaseService.interceptors.request.use(
  (config) => {
    const token = getAccessToken();

    if (token) {
      config.headers = config.headers || {};

      config.headers[REQUEST_HEADER_AUTH_KEY] = `${TOKEN_TYPE}${token}`;
    }

    return config;
  },

  (error) => Promise.reject(error),
);

/*
-----------------------------------------
RESPONSE INTERCEPTOR
-----------------------------------------
*/
BaseService.interceptors.response.use(
  (response) => {
    const sysMode = response?.data?.system_mode;

    // maintenance mode
    if (sysMode?.is_maintenance) {
      handleMaintenance(sysMode);
    }

    // notification mode
    else if (typeof sysMode?.is_notification === "boolean") {
      if (sysMode.is_notification) {
        store.dispatch(enableNotification());
      } else {
        store.dispatch(disableNotification());
      }
    }

    return response;
  },

  (error) => {
    const resp = error?.response;

    const sysMode = resp?.data?.system_mode;

    if (sysMode?.is_maintenance) {
      handleMaintenance(sysMode);
    } else if (typeof sysMode?.is_notification === "boolean") {
      if (sysMode.is_notification) {
        store.dispatch(enableNotification());
      } else {
        store.dispatch(disableNotification());
      }
    }

    // unauthorized
    if (resp && unauthorizedCode.includes(resp.status)) {
      store.dispatch(onSignOutSuccess());
    }

    return Promise.reject(error);
  },
);

export default BaseService;
